/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import {
  deployEnv,
  endpoints,
  isAws,
  isCpd,
  isWxoEnabled,
  wxoUrl
} from '@ibm-watson/shared/modules/ia/util/config';
import { DeployEnv } from '../../../ts';
import { PlanName } from '../../../ts/plan';
import { getServerBaseUri } from '../../ia/util/url';
import {
  FACEBOOK_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  PREVIEW_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  SMS_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
} from './channelTypes';

export const INTERCOM_OAUTH_URL = 'https://app.intercom.io/oauth';

const integrationEnvMappings = Object.freeze({
  [DeployEnv.DEV]: '-d',
  [DeployEnv.DEVAWS]: '-d',
  [DeployEnv.STAGE]: '-s',
  [DeployEnv.STAGEAWS]: '-s'
});

export const integrationEnv = integrationEnvMappings[deployEnv] || '';

export const location = 'public';

// Return the route to integrations within CPD.
export function buildIntegrationsUrlCpd() {
  // expecting a document.baseURI like https://zen-cpd-zen.apps.supercomputer6.os.fyre.ibm.com/assistant/watson-assistant---two
  const url = urlJoin(getServerBaseUri(), 'integrations');
  // the final url should look like https://zen-cpd-zen.apps.supercomputer6.os.fyre.ibm.com/assistant/watson-assistant---two/integrations
  return url;
}

// Return the route to static web chat resources for integrations within CPD.
export function buildWebchatUrlCpd() {
  // expecting a document.baseURI like https://zen-cpd-zen.apps.supercomputer6.os.fyre.ibm.com/assistant/watson-assistant---two
  const url = urlJoin(buildIntegrationsUrlCpd(), 'static', 'webchat');
  // the final url should look like https://zen-cpd-zen.apps.supercomputer6.os.fyre.ibm.com/assistant/watson-assistant---two/integrations/static/webchat
  return url;
}

// This method is in WxO and also other Cloud envs too
// so we need to deal with the case where it's WxO and not AWS.
export const getRegionValueForEnv = (region, deployEnv) => {
  if (deployEnv === DeployEnv.DEV) {
    return isWxoEnabled ? 'wxo-dev' : 'dev';
  } else if (deployEnv === DeployEnv.DEVAWS) {
    return 'aws-dev';
  } else if (deployEnv === DeployEnv.STAGEAWS) {
    return 'aws-staging';
  } else if (deployEnv === DeployEnv.STAGE) {
    return isWxoEnabled ? 'wxo-staging' : 'staging';
  } else if (deployEnv === DeployEnv.PREPRODUCTIONAWS) {
    return 'aws-preprod';
  } else if (
    deployEnv === DeployEnv.PRODUCTIONAWS ||
    deployEnv === DeployEnv.FRAPRODUCTIONAWS ||
    deployEnv === DeployEnv.SINPRODUCTIONAWS ||
    deployEnv === DeployEnv.MUMPRODUCTIONAWS
  ) {
    return `aws-${region}`;
  }
  return isWxoEnabled ? `wxo-${region}` : region;
};

export const getRegionValue = (region, environment = integrationEnv) => {
  if (environment === '-d') {
    return 'dev';
  }
  if (environment === '-s') {
    return 'staging';
  }

  if (region.indexOf('w3ibm:') !== -1) {
    // If we're in a syndicated/CIO instance https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/21709
    region = region.split(':').pop();
  }

  return region;
};

export const integrationBaseUrl = ({
  channelType,
  integrationId = undefined,
  region,
  locale = location, // This will be "public" unless you are in premium.
  environment = integrationEnv,
  subscriptionID = undefined,
  serviceInstanceID = undefined
}) => {
  // How we form these URLs: https://github.ibm.com/watson-engagement-advisor/wea-backlog/wiki/WA-Integrations-Public-Hostname-Strategy

  let environmentUrlSlug = ''; // In production, this is always an empty string. Dev and stage override this value.
  let localRegion = 'us-south'; // For dev and stage environments, this is always us-south. Production overrides this value.
  let regionPrefix = ''; // region is mapped as aws-{region} in AWS world for preview URL
  if (environment === '-d' || environment === '-s') {
    environmentUrlSlug = environment === '-d' ? '.dev' : '.test';
  } else {
    localRegion = region;
  }
  let url = null;
  let domain = 'watson.cloud.ibm.com';
  if (isAws) {
    domain = 'watson.appdomain.cloud';
    regionPrefix = 'aws-';
  }
  let builderInPath = ''; // WxO in IBM Cloud uses builder in urls
  if (isWxoEnabled && !isAws) {
    builderInPath = '-builder';
  }

  switch (channelType) {
    case PREVIEW_CHANNEL_TYPE:
      let baseURL = endpoints.preview;
      if (isCpd) {
        baseURL = urlJoin(buildIntegrationsUrlCpd(), 'static', 'webchat');
      } else if (isAws || isWxoEnabled) {
        // isAws should mean we have isWxoEnabled true too, but adding wxoEnabled check for WxO in IBM Cloud
        localRegion = getRegionValueForEnv(localRegion, deployEnv);
      } else {
        localRegion = getRegionValue(localRegion, environment);
      }
      url = `${baseURL}/preview.html?region=${localRegion}&integrationID=${integrationId}&serviceInstanceID=${serviceInstanceID}`;

      // If we are in wxo in either the CPD OR the CIO (AWS + a premium plan) contexts, we need to pass along information
      // to the WXO forms code to know where to post forms to. If there is a subscriptionID, that's how we know they are in
      // a premium-like plan.
      if (isWxoEnabled && (isCpd || (isAws && subscriptionID))) {
        // If we are in CPD with WXO enabled or in a wxo cloud plan, we need to pass the path for
        // Orchestrate to the preview link in order for the preview link to
        // form the orchestratePrivateHostURL needed in its embed snippet.
        const wxoUrlPathname = new URL(wxoUrl).pathname;
        url += `&orchestratePrivatePath=${wxoUrlPathname}`;

        // If we are in CIO, also need orchestratePrivateHostURL.
        if (isAws && subscriptionID) {
          url += `&orchestratePrivateHostURL=${wxoUrl}`;
        }
      }
      if (subscriptionID) {
        url += `&subscriptionID=${subscriptionID}`;
      }
      break;
    case SMS_CHANNEL_TYPE:
      // Twilio webhook URI has instance/message swapped
      url = `https://integrations.${regionPrefix}${localRegion}.assistant${builderInPath}${environmentUrlSlug}.${domain}/${locale}/${channelType}/${integrationId}/message`;
      break;
    case WHATSAPP_CHANNEL_TYPE:
      // Twilio webhook URI has instance/message swapped
      url = `https://integrations.${regionPrefix}${localRegion}.assistant${builderInPath}${environmentUrlSlug}.${domain}/${locale}/${channelType}/${integrationId}/message`;
      break;
    case PHONE_CHANNEL_TYPE:
      if (environment === '-d') {
        // dev016 slot has its own VG running and needs special routing.
        if (locale === '7ad616a350cc48078f17e3ee3df551de') {
          locale = `7ad616a350cc48078f17e3ee3df551de.dev16`;
        } else {
          locale = `${locale}`;
        }
      }
      if (isAws) {
        url = `sips:${locale}.voip.${region}${environmentUrlSlug}.aws.watsonassistant.ibm.com`;
      } else {
        url = `sips:${locale}.voip.${localRegion}.assistant${builderInPath}${environmentUrlSlug}.watson.cloud.ibm.com`;
      }
      break;
    default:
      url = `https://integrations.${regionPrefix}${localRegion}.assistant${builderInPath}${environmentUrlSlug}.${domain}/${locale}/${channelType}/message/${integrationId}`;
  }
  return url;
};

export const urlFromMicroservice = ({
  integrationId,
  region,
  plan,
  premiumPlanIntegrationOrgId,
  channelType
}) => {
  // https://github.ibm.com/watson-engagement-advisor/wea-backlog/wiki/WA-Integrations-Public-Hostname-Strategy
  const config = {
    channelType,
    integrationId,
    region,
    locale:
      (plan === PlanName.PREMIUM ||
        plan === PlanName.ENTERPRISE_DATA_ISOLATION) &&
      premiumPlanIntegrationOrgId
        ? premiumPlanIntegrationOrgId
        : 'public'
  };
  return integrationBaseUrl(config);
};

export const oneCloudUrl = (
  integration,
  plan,
  premiumPlanIntegrationOrgId,
  region
) => {
  const supportedChannelTypes = [FACEBOOK_CHANNEL_TYPE, SLACK_CHANNEL_TYPE];
  if (
    !_.isNil(_.get(integration, 'integration_id')) &&
    supportedChannelTypes.includes(integration.type)
  ) {
    return urlFromMicroservice({
      integrationId: integration.integration_id,
      region,
      plan,
      premiumPlanIntegrationOrgId,
      channelType: integration.type
    });
  }
  return null;
};
